import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import UnitBoxLoader from './UnitBoxLoader'
import useUnitInitChannelsDataUnit from '../../../Hooks/useUnitInitChannelsData'
import UnitTimeBasedChart from '../../Charts/UnitTimeBasedChart'
import UnitBoxChartSignalR from '../../SignalrSubscribes/UnitBoxChartSignalR'
import { useAuth } from '../../../Contexts/AuthProvider'
import UnitBoxChartHeader from './UnitBoxChartHeader'
import ManageAxesModal from '../../modals/ManageAxisModal/ManageAxesModal'
import ManageDateFormatModal from '../../modals/ManageDateFormatModal/ManageDateFormatModal'
import UnitBoxDataError from './UnitBoxDataError'
import { Resizable } from 'react-resizable'
import EventEmitter from '../../../utils/EventEmitter'
import { useBoxes } from '../../../Contexts/BoxContext';

const defaultChartHeight = 450

const UnitBoxChart = props => {

  const {
    boxData,
    onRemove,
    handleEdit,
    dragHandleProps,
    onNewWindowOpen,
    onExportToCsv,
    section = 'unit',
    onUpdateSettings
  } = props

  const {
    unitUid,
    channels,
    yAxisSettings,
    xAxisSettings,
    timeType,
    timeFormat,
    filters,
  } = boxData

  const { token, currentCompany } = useAuth()

  const [showManageAxesModal, setShowManageAxesModal] = useState(false)
  const [showManageDateFormatModal, setManageDateFormatModal] = useState(false)
  const [chartHeight, setChartHeight] = useState(null)
  const [data, setData] = useState(null)
  const [hasDataError, setHasDataError] = useState(false)
  const [currentTimestamp, setCurrentTimestamp] = useState(null)
  const [dateTimeFormat, setDateTimeFormat] = useState('')
  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(null)
  const { startDate, endDate } = useBoxes();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const hasTriggeredRefetch = useRef(false);
  const reconnectCount = useRef(0);
  const isNormalDataUpdate = useRef(false);
  const lastDataUpdateTime = useRef(0);
  const signalrRequestBody = useMemo(() => ({
    uId: unitUid,
    channels, ...filters,
    jobType: section,
    dateFrom: startDate,
    dateTo: endDate,
    jobId: filters.jobId
  }), [unitUid, channels, section, startDate, endDate, filters.jobId])

  const [exportImage, setExportImage] = useState(false);

  const handleExportToImage = () => {
    setExportImage(true);
    setTimeout(() => setExportImage(false), 1000); 
};

  

  const channelsDataUnitRequest = useUnitInitChannelsDataUnit({ unitUid, channels, filters, section, startDate,  endDate  })

  useEffect(() => {
    setChartHeight(defaultChartHeight)
  }, [])

  // Скидаємо флаг нормального оновлення при зміні параметрів запиту
  useEffect(() => {
    isNormalDataUpdate.current = false;
    lastDataUpdateTime.current = 0;
  }, [unitUid, channels, section, startDate, endDate])

  const chartResizeConfig = useMemo(() => (
    {
      onResize: (e, dir, reftoEl) => {
        setChartHeight(reftoEl.offsetHeight)
      },
      enable: {
        top: false,
        right: false,
        bottom: true,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      },
      minHeight: 200,
    }
  ), [])

  useEffect(() => {
    if (channelsDataUnitRequest.isSuccess) {
      const {
        channelDataResponses,
        lastProcessedTimestamp,
        dateTimeFormat,
        jobId,
      } = channelsDataUnitRequest.data

      if (jobId) {
        EventEmitter.emit('InitUnitBoxChartWithJobId', jobId)
      }

      const serverDataMap = {}

      if (!channelDataResponses) {
        setHasDataError(true)
        return
      }

      channelDataResponses.forEach(channelData => {
        const { channel, data } = channelData
        serverDataMap[channel.code] = data
      })
      
      isNormalDataUpdate.current = true;
      lastDataUpdateTime.current = Date.now();
      
      setData(serverDataMap)
      setCurrentTimestamp(lastProcessedTimestamp)
      setDateTimeFormat(dateTimeFormat)

      if (isReconnecting) {
        setIsReconnecting(false);
        hasTriggeredRefetch.current = false;
        reconnectCount.current = 0;
      }

    }
  }, [channelsDataUnitRequest.data, channelsDataUnitRequest.isSuccess, isReconnecting])
 
  useEffect(() => {
    if (data === null && !channelsDataUnitRequest.isFetching && !hasTriggeredRefetch.current && reconnectCount.current < 3 && !isNormalDataUpdate.current) {
      hasTriggeredRefetch.current = true;
      reconnectCount.current += 1;
      
      if (channelsDataUnitRequest.refetch) {
        channelsDataUnitRequest.refetch()
      }
    }
    
    if (data !== null) {
      hasTriggeredRefetch.current = false;
      reconnectCount.current = 0;
      isNormalDataUpdate.current = false;
    }
  }, [data, channelsDataUnitRequest.isFetching])

  const handleLiveData = useCallback(incomingData => {
    if (incomingData && incomingData.length) {
      setData(prevData => {
        let hasLargeDataBatch = false;
        incomingData.forEach((channelData) => {
          const { channel, data } = channelData;
          if (data.length > 3) {
            hasLargeDataBatch = true;
          }
        });

        const currentTime = Date.now();
        const timeSinceLastUpdate = currentTime - lastDataUpdateTime.current;
        const isRecentUpdate = timeSinceLastUpdate < 5000;

        if (hasLargeDataBatch && prevData && !isReconnecting && !isRecentUpdate) {
          setIsReconnecting(true);

          setTimeout(() => {
            setIsReconnecting(false);
          }, 5000);

          setTimeout(() => {
            if (channelsDataUnitRequest.refetch) {
              channelsDataUnitRequest.refetch();
            }
          }, 1000);

          return null;
        }

        const newData = prevData ? { ...prevData } : {}
        incomingData.forEach((channelData) => {
          const { channel, data } = channelData
          const existingChannelData = newData[channel.code] || []
          newData[channel.code] = [...existingChannelData, ...data]
        })
        
        lastDataUpdateTime.current = currentTime;
        
        return newData
      })
    }
  }, [isReconnecting, channelsDataUnitRequest.refetch])

  const openManageAxesModal = () => {
    setShowManageAxesModal(true)
  }

  const saveAxesSettings = (yAxisSettings, xAxisSettings, timeType, timeFormat) => {
    boxData.yAxisSettings = yAxisSettings
    boxData.xAxisSettings = xAxisSettings
    boxData.timeType = timeType
    boxData.timeFormat = timeFormat
    if (onUpdateSettings) onUpdateSettings()
  }

  const saveDateFormat = (timeType, timeFormat) => {
    boxData.timeType = timeType
    boxData.timeFormat = timeFormat
    if (onUpdateSettings) onUpdateSettings()
  }

  const content = hasDataError
    ? <UnitBoxDataError/>
    : (
      <Resizable {...chartResizeConfig}>
        <UnitTimeBasedChart
          id={boxData.id}
          startTime={currentTimestamp}
          channels={channels}
          channelsData={data}
          chartHeight={chartHeight}
          timeType={timeType}
          timeFormat={timeFormat}
          yAxisSettings={yAxisSettings}
          xAxisSettings={xAxisSettings}
          dateTimeFormat={dateTimeFormat}
          onExportImage={exportImage} 
        />
      </Resizable>
    )

  return (
    <Fragment>
      <UnitBoxLoader
        loading={channelsDataUnitRequest.isLoading || channelsDataUnitRequest.isFetching}
      />

      <UnitBoxChartHeader
        box={boxData}
        onRemove={onRemove}
        handleEdit={handleEdit}
        dragHandleProps={dragHandleProps}
        onManageAxesSettings={openManageAxesModal}
        onNewWindowOpen={onNewWindowOpen}
        onExportToCsv={onExportToCsv}
        onExportToImage={handleExportToImage} 
      />

      <div className="pageCard">
        {channelsDataUnitRequest?.data?.noData ? (
          <div style={{
            paddingTop: '30px',
            color: 'rgb(144, 144, 144)',
            marginBottom: '20px', textAlign: 'center'
          }}>No data found.</div>
        ) : (
          <div>{content}</div>
        )}
      </div>

      <ManageAxesModal
        isOpen={showManageAxesModal}
        setOpen={setShowManageAxesModal}
        editedModel={boxData}
        reverseChart={false}
        onSave={saveAxesSettings}
        timeType={timeType}
        timeFormat={timeFormat}
      />

      <ManageDateFormatModal
        isOpen={showManageDateFormatModal}
        setOpen={setManageDateFormatModal}
        onSave={saveDateFormat}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
        timeType={timeType}
        timeFormat={timeFormat}
        type="chartView"
        chartType="timeBased"
      />

      {
        (currentTimestamp && !channelsDataUnitRequest.isFetching && !isReconnecting && data) && (
          <>
            <UnitBoxChartSignalR
              token={token}
              companyId={currentCompany?.id}
              requestBody={signalrRequestBody}
              onIncomingData={handleLiveData}
              lastProcessedTimestamp={currentTimestamp}
              section={section}
              onReconnect={() => {}}
            />
          </>
        )
      }

    </Fragment>
  )
}

export default UnitBoxChart
